/**
 * @fileoverview The reader's width toggle: the page at its reading width, or
 * wide. Operator ask, 2026-10-16, after a Claude artifact's width control.
 *
 * THE READER'S, NOT THE PAGE'S. It changes nothing published: the author's
 * measure stays the reading width, and the choice lives in this browser only.
 * Remembered per viewer in localStorage, read and written inside try/catch, so
 * a browser that refuses storage still toggles and simply forgets.
 *
 * WIDE IS ITS OWN VALUE, `--pages-reader-wide`, not the author's `measure-wide`,
 * which sizes a page whose author chose the wide layout and must not move
 * under them. The stylesheet supplies the reader's wide value, so the button
 * is on every page. It is hidden only when the two widths come out the same at
 * this viewport (a page already that wide, or a phone, where every page is
 * full width anyway): a control that does
 * nothing visible is worse than none. Measured by two hidden probes inside the
 * page, so they resolve the page's own theme tokens, and re-measured on resize.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const WIDTH_STORAGE_KEY = 'pages:reader-width';

/** [wide, setWide], remembered across pages for this viewer. */
export function useReaderWidth(): [boolean, (wide: boolean) => void] {
  const [wide, setWideState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WIDTH_STORAGE_KEY) === 'wide';
    } catch {
      return false;
    }
  });
  const setWide = useCallback((next: boolean) => {
    setWideState(next);
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, next ? 'wide' : 'reading');
    } catch {
      /* forgotten on the next page, and that is all */
    }
  }, []);
  return [wide, setWide];
}

export function WidthToggle({
  wide,
  onChange,
  readingMeasure = 'var(--pages-measure)',
}: {
  wide: boolean;
  onChange: (wide: boolean) => void;
  /** The CSS width the page reads at when not wide; a page may choose narrow. */
  readingMeasure?: string;
}) {
  const reading = useRef<HTMLSpanElement>(null);
  const wider = useRef<HTMLSpanElement>(null);
  const [differs, setDiffers] = useState(false);

  const measure = useCallback(() => {
    const small = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 48rem)').matches;
    const a = reading.current?.offsetWidth ?? 0;
    const b = wider.current?.offsetWidth ?? 0;
    setDiffers(!small && Math.abs(a - b) > 1);
  }, []);

  useLayoutEffect(measure, [measure, readingMeasure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // FIXED, so a probe as wide as the window never widens the document.
  const probe = {
    position: 'fixed',
    left: 0,
    top: 0,
    visibility: 'hidden',
    height: 0,
    width: '100vw',
    pointerEvents: 'none',
  } as const;

  return (
    <>
      <span
        aria-hidden="true"
        data-probe="reading"
        ref={reading}
        style={{ ...probe, maxWidth: readingMeasure }}
      />
      <span
        aria-hidden="true"
        data-probe="wide"
        ref={wider}
        style={{ ...probe, maxWidth: 'var(--pages-reader-wide)' }}
      />
      {differs && (
        <button
          type="button"
          className="pages-width-toggle"
          aria-pressed={wide}
          aria-label="Wide view"
          title={wide ? 'Back to reading width' : 'Wide view'}
          onClick={() => onChange(!wide)}
        >
          <span aria-hidden="true">{wide ? '→←' : '←→'}</span>
          <span className="pages-width-toggle-text">{wide ? 'Reading width' : 'Wide'}</span>
        </button>
      )}
    </>
  );
}
